import { useEffect, useRef, useState } from 'react';
import { Graphic, MapView, Point } from '../constructors';
import { createPointGraphic } from '../helpers/graphics';
import { parseMapLocationKey } from '../helpers/locations';
import type { CustomMapMarker } from '../types';

export type EsriMapTooltip = {
    label: string;
    posX: number;
    posY: number;
};

type EsriMapGraphicsState = {
    markers: CustomMapMarker[];
    selectedId: string | undefined;
    tooltip: EsriMapTooltip | undefined;
};

type EsriMapMarkerInterface = {
    currentTooltip: EsriMapTooltip | null;
    updateSelectedMarker: (markerId: string) => void;
    updateMarkers: (markers: CustomMapMarker[]) => void;
};

export const useEsriMapMarkers = (
    initialMarkers: CustomMapMarker[],
    mapViewController?: MapView,
    subpremiseSupport?: boolean
): EsriMapMarkerInterface => {
    const [graphicsState, setGraphicsState] = useState<EsriMapGraphicsState>({
        markers: initialMarkers,
        selectedId: undefined,
        tooltip: undefined,
    });

    const stateRef = useRef(graphicsState);

    useEffect(() => {
        stateRef.current = graphicsState;
    }, [graphicsState]);

    useEffect(
        function initializeTooltipDetection(): void {
            if (mapViewController === undefined) {
                return;
            }
            mapViewController.on('pointer-move', async (event) => {
                const query = await mapViewController.hitTest(event);

                if (query.results.length === 0) {
                    setGraphicsState((last) => ({ ...last, tooltip: undefined }));
                    return;
                }

                const markerResult = query.results.find((r) => {
                    return 'graphic' in r ? !!r.graphic.attributes.isMarker : undefined;
                });

                if (!markerResult) {
                    setGraphicsState((last) => ({ ...last, tooltip: undefined }));
                    return;
                }

                const marker = stateRef.current?.markers.find((marker) => {
                    if ('graphic' in markerResult) {
                        return marker.id === markerResult.graphic.attributes.id;
                    }
                    return undefined;
                });

                const shouldSetGraphicsState =
                    marker !== undefined &&
                    stateRef.current.tooltip?.label !== marker.tip &&
                    'graphic' in markerResult;

                if (shouldSetGraphicsState) {
                    const markerLocation = mapViewController.toScreen(
                        new Point({
                            x: markerResult.graphic.geometry.get('x'),
                            y: markerResult.graphic.geometry.get('y'),
                        })
                    );

                    setGraphicsState((last) => ({
                        ...last,
                        tooltip: {
                            label: marker.tip,
                            posX: markerLocation.x - 15,
                            posY: markerLocation.y + 30,
                        },
                    }));
                }
            });
        },
        [mapViewController]
    );

    useEffect(
        function initializeMarkerHitDetection(): void {
            if (mapViewController === undefined) {
                return;
            }

            mapViewController.on('click', async (event) => {
                const query = await mapViewController.hitTest(event.mapPoint);

                if (query.results.length === 0) {
                    return;
                }

                const markerResult = query.results.find((r) => {
                    return 'graphic' in r ? !!r.graphic.attributes.isMarker : undefined;
                });

                if (markerResult !== undefined) {
                    const id =
                        'graphic' in markerResult ? markerResult.graphic.attributes.id : undefined;
                    const { index: newSelectedIndex } = parseMapLocationKey(id);
                    const newSelectedId =
                        newSelectedIndex >= 0
                            ? 'graphic' in markerResult
                                ? markerResult.graphic.attributes.id
                                : undefined
                            : undefined;

                    if (newSelectedId !== stateRef.current.selectedId) {
                        setGraphicsState((last) => ({
                            ...last,
                            selectedId: newSelectedId,
                        }));
                    }
                }
            });
        },
        [stateRef, mapViewController]
    );

    useEffect(
        function refreshMapMarkerPlacement(): void {
            const markers = graphicsState.markers;

            if (!mapViewController || markers.length === 0) {
                return;
            }

            mapViewController?.graphics.removeAll();

            const tempGraphics: Graphic[] = [];
            const selectedMarkerId = graphicsState.selectedId;

            markers.forEach((marker) => {
                const markerIsSelected = marker.id === graphicsState.selectedId;
                if (!markerIsSelected) {
                    const { markerGraphic, labelGraphic } = createPointGraphic({
                        marker,
                        selectedMarkerId,
                    });
                    if (markerGraphic) {
                        mapViewController.graphics.add(markerGraphic);
                        tempGraphics.push(markerGraphic);
                        if (labelGraphic) {
                            mapViewController.graphics.add(labelGraphic);
                            tempGraphics.push(labelGraphic);
                        }
                    }
                }
            });

            const selectedGraphic = markers.find(
                (marker) => marker.id === graphicsState.selectedId
            );

            // Ensure that the selected graphic is always on the top.
            if (selectedGraphic !== undefined) {
                const { markerGraphic, labelGraphic } = createPointGraphic({
                    marker: selectedGraphic,
                    selectedMarkerId,
                });
                if (markerGraphic) {
                    mapViewController.graphics.add(markerGraphic);
                    tempGraphics.push(markerGraphic);
                    if (labelGraphic) {
                        mapViewController.graphics.add(labelGraphic);
                        tempGraphics.push(labelGraphic);
                    }
                }
            }
        },
        [mapViewController, graphicsState.selectedId, graphicsState.markers, subpremiseSupport]
    );

    function updateSelectedMarker(markerId: string): void {
        setGraphicsState((last) => ({ ...last, selectedId: markerId }));
    }

    function updateMarkers(markers: CustomMapMarker[]): void {
        setGraphicsState((last) => ({ ...last, markers }));
    }

    return {
        updateSelectedMarker,
        updateMarkers,
        currentTooltip: graphicsState.tooltip !== undefined ? graphicsState.tooltip : null,
    };
};
